import type { LibraryKey } from '../types'

/**
 * The libraries, as the pages need to know them: the three people browse,
 * and the Egnyte Inbox only administrators see.
 *
 * The database holds the same list (public.libraries) with the one fact the
 * Worker needs -- where a new tag goes. What is here is only how each one is
 * shown, which is the interface's business.
 */
export interface LibraryDef {
  key: LibraryKey
  label: string
  /** Where its page is. */
  route: string
  /** Categories as filter rows. Visual Language has one flat row of tags. */
  hasCategories: boolean
  /** The packaging / non-packaging buttons, which only mean anything in Audit. */
  hasPackaging: boolean
  /** Where a tag goes when nobody has said: the database's libraries.default_group. */
  defaultGroup: string
  /** Put in front of a new category's slug. Slugs are unique across libraries. */
  groupPrefix: string
  /** Where the search box points, and what an empty library says. */
  searchHint: string
  emptyHint: string
  /**
   * Not a library people browse: the Egnyte Inbox, where pictures from Egnyte
   * wait for an administrator (Huanrui, 2026-10-08). Left out of Image Tag's
   * choice of library; its page is behind the administrators' skill.
   */
  internal?: boolean
  /**
   * Categories not filled in for every picture yet, marked in the filter with
   * an asterisk at half strength (Huanrui, 2026-10-08): Design Works' Product,
   * Format and Type, which pictures from Egnyte arrive without and are given
   * by hand later.
   */
  unfinishedGroups?: string[]
  /**
   * Several tags chosen in one category narrow (AND) rather than widen (OR).
   * Visual Language (Huanrui, 2026-10-14): it has categories now -- one,
   * Other -- and keeps the rule it had of every chosen tag at once.
   */
  andWithin?: boolean
}

export const LIBRARIES: LibraryDef[] = [
  {
    key: 'audit',
    label: 'Audit',
    route: '/library',
    hasCategories: true,
    hasPackaging: true,
    defaultGroup: 'other',
    groupPrefix: '',
    searchHint: 'Search the audit library…',
    emptyHint: 'The library is empty. Run Image Tag on a Miro board to fill it.',
  },
  {
    key: 'visual-language',
    label: 'Visual Language',
    route: '/visual-language',
    // A filter like the others since 2026-10-14: one category, Other, for now.
    hasCategories: true,
    andWithin: true,
    hasPackaging: false,
    defaultGroup: 'vl-tags',
    groupPrefix: 'vl-',
    searchHint: 'Search visual language…',
    emptyHint: 'Nothing here yet. Run Image Tag on a Miro board and choose Visual Language.',
  },
  {
    key: 'design-works',
    label: 'Design References',
    route: '/design-works',
    hasCategories: true,
    hasPackaging: false,
    defaultGroup: 'dw-other',
    groupPrefix: 'dw-',
    unfinishedGroups: ['dw-product', 'dw-format', 'dw-type'],
    searchHint: 'Search design works…',
    emptyHint: 'Nothing here yet. Run Image Tag on a Miro board and choose Design References.',
  },
  {
    key: 'egnyte',
    label: 'Egnyte Inbox',
    route: '/egnyte',
    hasCategories: true,
    hasPackaging: false,
    defaultGroup: 'eg-project-name',
    groupPrefix: 'eg-',
    searchHint: 'Search what came in from Egnyte…',
    emptyHint: 'Nothing has come in from Egnyte yet. The importer puts design work here, tagged by its folders.',
    internal: true,
  },
]

export const libraryDef = (key: LibraryKey): LibraryDef =>
  LIBRARIES.find((one) => one.key === key) ?? LIBRARIES[0]
